import {sendEvent} from '@github-ui/hydro-analytics'
import {useEffect, useImperativeHandle, useRef} from 'react'

export type FormattingTools = {
  header: () => void
  bold: () => void
  italic: () => void
  quote: () => void
  code: () => void
  link: () => void
  unorderedList: () => void
  orderedList: () => void
  taskList: () => void
  mention: () => void
  reference: () => void
}

const sendFormatEvent = (tool: keyof FormattingTools) => sendEvent(`markdown_editor.format`, {tool})

type FormattingToolsProps = {
  forInputId: string
  ref?: React.Ref<FormattingTools>
}

/**
 * Renders an invisible `markdown-toolbar-element` that provides formatting actions to the
 * editor. This is a hacky way of using the library, but it allows us to use the built-in
 * behavior without having to actually display the inflexible toolbar element. It also means
 * we can still use the formatting tools even if the consumer hides the default toolbar
 * buttons (ie, by keyboard shortcut).
 */
export const FormattingTools = ({ref: forwadedRef, forInputId}: FormattingToolsProps) => {
  useEffect(() => {
    // requiring this module will register the custom element; we don't want to do that until the component mounts in the DOM
    void import('@github/markdown-toolbar-element')
  }, [])

  const headerRef = useRef<HTMLElement>(null)
  const boldRef = useRef<HTMLElement>(null)
  const italicRef = useRef<HTMLElement>(null)
  const quoteRef = useRef<HTMLElement>(null)
  const codeRef = useRef<HTMLElement>(null)
  const linkRef = useRef<HTMLElement>(null)
  const unorderedListRef = useRef<HTMLElement>(null)
  const orderedListRef = useRef<HTMLElement>(null)
  const taskListRef = useRef<HTMLElement>(null)
  const mentionRef = useRef<HTMLElement>(null)
  const referenceRef = useRef<HTMLElement>(null)

  useImperativeHandle(forwadedRef, () => ({
    header: () => {
      headerRef.current?.click()
      sendFormatEvent('header')
    },
    bold: () => {
      boldRef.current?.click()
      sendFormatEvent('bold')
    },
    italic: () => {
      italicRef.current?.click()
      sendFormatEvent('italic')
    },
    quote: () => {
      quoteRef.current?.click()
      sendFormatEvent('quote')
    },
    code: () => {
      codeRef.current?.click()
      sendFormatEvent('code')
    },
    link: () => {
      linkRef.current?.click()
      sendFormatEvent('link')
    },
    unorderedList: () => {
      unorderedListRef.current?.click()
      sendFormatEvent('unorderedList')
    },
    orderedList: () => {
      orderedListRef.current?.click()
      sendFormatEvent('orderedList')
    },
    taskList: () => {
      taskListRef.current?.click()
      sendFormatEvent('taskList')
    },
    mention: () => {
      mentionRef.current?.click()
      sendFormatEvent('mention')
    },
    reference: () => {
      referenceRef.current?.click()
      sendFormatEvent('reference')
    },
  }))

  return (
    <markdown-toolbar for={forInputId} style={{display: 'none'}}>
      <md-header ref={headerRef} />
      <md-bold ref={boldRef} />
      <md-italic ref={italicRef} />
      <md-quote ref={quoteRef} />
      <md-code ref={codeRef} />
      <md-link ref={linkRef} />
      <md-unordered-list ref={unorderedListRef} />
      <md-ordered-list ref={orderedListRef} />
      <md-task-list ref={taskListRef} />
      <md-mention ref={mentionRef} />
      <md-ref ref={referenceRef} />
    </markdown-toolbar>
  )
}
FormattingTools.displayName = 'MarkdownEditor.FormattingTools'
